import {Dict} from "wfr/commons/common"

/**
 * Listes des langues de navigation supportées
 */
export enum ENavLanguages{
	fr="fr", en="en"
}

/**
 * Dict de labels
 */
export type L10nLabels = {
	[key in ENavLanguages]: Dict<((...args:any)=>string)|string>
}


/**
 * Dict localisé de locInfos (pour panel, group, olProduct et message)
 */
export type JLocInfos = {
	[key in ENavLanguages]: JLocInfo
}

/**
 * Infos localisée (pour panel, group, olProduct et message)
 */
export interface JLocInfo {
	title?:string
	body?:string
}

/**
 * Dépendance d'un product
 */
export interface JDownloadDep {
	firstMinorVersion:number,
	product:string
}

/**
 * Structure d'une page de téléchargements
 */
export interface JProductStructure {
	panels:JProductPanel[]
	groups: JProductGroup[]
}

/**
 * Panel de téléchargement
 */
export interface JProductPanel {
	code:string
	locInfos:JLocInfos,
	groups:JProductGroup[]
}

/**
 * Groupe de téléchargement
 */
export interface JProductGroup {
	locInfos:JLocInfos
	code:string
	iconClass:string
	regexps:string[]
	olProducts?:string[]
	grpOthers?:boolean
	collapsed?:boolean
}

/**
 * Référence d'un produit
 */
export interface JProductRef {
	majorVersion:string
	minorVersion:string
	path:string
	productKey:string
}

/**
 * Métas génériques du depot
 */
export interface JDepotMetas {
	deployedBy:string
	deployDate:number
	path:string
	processing:string

	updateDate?:number
	updatedBy?:string
	unlisted?:string
	olderResId?:string
}

/**
* Liste des productTypes
*/
export enum EProductType{
	application="application", extension="extension", server="server", devtool="devtool"
}

/**
 * Liste des extTypes
 */
export enum EExtType{
	skin="skin", gen="gen", mod="mod"
}

export enum ENature{
	generic="generic", dedicated="dedicated"
}

/**
 * Métas communes doc/download
 */
export interface JCommonMetas extends JDepotMetas {
	majorVersion:string
	productKey:string
	deprecated:boolean
	status:"product"|"project"|"lab"
	nature?:ENature
	productType:EProductType
	extType?:EExtType
}

/**
 * Métas spécifiques de doc
 */
export interface JDocProductMetas extends JCommonMetas {
	downloadUrl:string
	publisher:JOrganization
	contributors?:JOrganization[]
	supportDuration:number
	docLocInfos:JDocLocInfos
	esDocProductCustomId:string
}
/**
 * Organisation (pour meta éditor/contributors)
 */
export interface JOrganization{
	name:string
	url:string
}
/**
 * Dict localisé de documentations
 */
export type JDocLocInfos = {
	[key in ENavLanguages]: JDocLocInfo
}
/**
 * Infos d'une documentation localisée
 */
export interface JDocLocInfo extends JLocInfo{
	title:string //title obligatoire
	tagline?:string
	license?:string
	state: "draft"|"valid";
}


export type JDownloadLocInfos = {
	[key in ENavLanguages]: JDownloadLocInfo
}
/**
 * loc infos stockées dans Doc
 */
export interface JDownloadLocInfo extends JLocInfo{
	title:string //title obligatoire
	body:null    //body absent
	tagline?:string
}

/**
 * Métas spécifiques de download
 */
export interface JDownloadProductMetas extends JCommonMetas {
	minorVersion:string
	releaseDate:string
	stage:EStage,
	releaseNote?:string
	structure?:JProductStructure
	deps:JDownloadDep[]
	family:JProductRef[]
	downloadLocInfos:JDownloadLocInfos
	docProviderUrl:string
	esProductId:string
	sortKey?:string
}

/**
 * stage d'un produit à télécharger
 */
export enum EStage {
	final="final", rc="rc", beta="beta", alpha="alpha"
}

/**
 * Infos génériques remontées par les APIs nodeInfos du depot
 */
export interface JInfos {
	n:string
	v:string
	resId:string
	path:string
	publicUrl:string
	unlisted?:boolean
}
/**
 * Fields génériques d'un productInfos
 * M -> Metas JDownloadProductMetas ou JDocProductMetas
 * C -> child, JDlInfos ou JInfos
 */
export interface JProductInfos<M,C> extends JInfos{
	ch:C[]
	hist:JProductInfosHist<M>[]
	metas:M
	succ:JProductInfosSucc<M>[]
}
/**
 * Histo d'un ProductInfos
 */
export interface JProductInfosHist<M> {
	v:string,
	unlisted?:boolean
	metas:M
}

/**
 * Successeurs d'un productInfos
 */
export interface JProductInfosSucc<M> extends JProductInfosHist<M> {
	succ:JProductInfosSucc<M>[]
}
/**
 * structure d'un lien de téléchargement
 */
export interface JDlInfos extends JInfos{
	prc:"olProduct"|"dlProduct"
	s:number
	metas:JDlMetas
}
/**
 * Métas d'un ol/dl Product (lien de téléchargement)
 */
export interface JDlMetas extends JDepotMetas{
	lang?:string
	os:string
	translationCompletion?:number
	locInfos?:JLocInfos
	exclude?:boolean
	esParentId:string
}

export interface JDownloadProductSource {
	productKey:string
	majorVersion:string
	minorVersion:string
	stage:EStage
	productType:EProductType
	extType?:EExtType
	nature?:ENature
	releaseDate:string
	path:string,
	docProviderUrl:string
	downloadLocInfos:JDownloadLocInfos,
	sortKey?:string
}

export interface JWeblateTranslation{
	total:number,
	total_words: number,
	total_chars: number,
	last_change: string //ex : 2026-05-06T14:36:27.241720Z
	recent_changes: number,
	translated: number,
	translated_words: number,
	translated_percent: number, // Exprimé entre 0 et 100
	translated_words_percent: number, //Exprimé entre 0 et 100
	translated_chars: number,
	translated_chars_percent: number,  //Exprimé entre 0 et 100
	fuzzy: number,
	fuzzy_percent: number, //Exprimé entre 0 et 100
	fuzzy_words: number,
	fuzzy_words_percent: number,
	fuzzy_chars: number,
	fuzzy_chars_percent: number,
	failing: number,
	failing_percent: number
	approved: number,
	approved_percent: number,
	approved_words: number,
	approved_words_percent: number,
	approved_chars: number,
	approved_chars_percent: number,
	readonly: number,
	readonly_percent: number,
	readonly_words: number,
	readonly_words_percent: number,
	readonly_chars: number,
	readonly_chars_percent: number,
	suggestions: number,
	comments: number,
	code: string, // nl
	name: string, // Dutch
	url: string, // https://l10n.scenari.software/weblate/projects/opale-25/-/nl/
	translate_url: string // "https://l10n.scenari.software/weblate/translate/opale-25/-/nl/"
}